<!-- Выбор калькулятора для следующей сохранённой версии ПЛАНа заказа. -->
<template>
  <div ref="root" class="plan-calculator-selector">
    <span class="plan-calculator-selector__label">Калькулятор</span>
    <button
      v-if="!readonly"
      class="plan-calculator-selector__trigger"
      type="button"
      aria-haspopup="listbox"
      :aria-expanded="open"
      title="Сменить калькулятор — ПЛАН пересчитается в новую версию"
      @click="toggle"
      @keydown.esc.stop="close"
    >
      <span class="plan-calculator-selector__trigger-name">
        {{ currentName }}
      </span>
      <span class="plan-calculator-selector__trigger-icon" aria-hidden="true">▾</span>
    </button>
    <strong v-else class="plan-calculator-selector__value">
      {{ currentName }}
    </strong>

    <div
      v-if="open"
      class="plan-calculator-selector__popover"
      role="dialog"
      aria-label="Калькулятор для ПЛАНа"
      @keydown.esc.stop="close"
    >
      <div class="plan-calculator-selector__heading">
        Калькулятор для этого заказа
      </div>

      <div v-if="optionsLoading" class="plan-calculator-selector__empty">
        Загружаем калькуляторы…
      </div>
      <div v-else class="plan-calculator-selector__options" role="listbox">
        <button
          v-for="preset in presets"
          :key="preset.id"
          class="plan-calculator-selector__option"
          :class="{ 'plan-calculator-selector__option--selected': isSelected(preset) }"
          type="button"
          role="option"
          :aria-selected="isSelected(preset)"
          @click="pendingPresetId = preset.id"
        >
          <span class="plan-calculator-selector__mark" aria-hidden="true">
            {{ optionMark(preset) }}
          </span>
          <span class="plan-calculator-selector__option-copy">
            <span class="plan-calculator-selector__option-name">
              {{ preset.name }}
              <span
                v-if="isDefault(preset)"
                class="plan-calculator-selector__default"
              >
                По умолчанию для новых заказов
              </span>
            </span>
            <small>{{ optionDescription(preset) }}</small>
          </span>
        </button>
        <div v-if="presets.length === 0" class="plan-calculator-selector__empty">
          Нет доступных калькуляторов
        </div>
      </div>

      <div v-if="hasPendingChange" class="plan-calculator-selector__warning">
        Изменение коснётся только этого заказа. Выбран «{{ selectedPreset.name }}».
        ПЛАН пересчитается в
        <strong>v{{ nextVersion }}</strong>, v{{ currentVersion }} останется в истории.
        <span v-if="changedCount">
          Изменённые для заказа исходные данные ({{ changedCount }}) относятся к
          текущему калькулятору и не перенесутся.
        </span>
      </div>

      <div class="plan-calculator-selector__actions">
        <PlanCalculationActionButton appearance="soft" @click="close">
          Отмена
        </PlanCalculationActionButton>
        <PlanCalculationActionButton
          v-if="hasPendingChange"
          :disabled="isCalculating"
          @click="confirm"
        >
          {{ isCalculating ? "Пересчитываем…" : `Сменить и пересчитать → v${nextVersion}` }}
        </PlanCalculationActionButton>
      </div>

    </div>
  </div>
</template>

<script>
import PlanCalculationActionButton from "@/components/Order/PlanCalculation/Buttons/Action.vue";

export default {
  name: "OrderPlanCalculationCalculatorSelector",

  components: { PlanCalculationActionButton },

  props: {
    presets: { type: Array, default: () => [] },
    currentPresetId: { type: [String, Number], default: null },
    defaultPresetId: { type: [String, Number], default: null },
    currentName: { type: String, default: "Калькулятор" },
    currentVersion: { type: Number, required: true },
    changedCount: { type: Number, default: 0 },
    isCalculating: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    optionsLoading: { type: Boolean, default: false },
  },

  emits: ["switch", "open"],

  data() {
    return {
      open: false,
      pendingPresetId: this.currentPresetId,
    };
  },

  computed: {
    nextVersion() {
      return this.currentVersion + 1;
    },
    selectedPreset() {
      return this.presets.find(
        (preset) => String(preset.id) === String(this.pendingPresetId)
      ) || null;
    },
    hasPendingChange() {
      return Boolean(this.selectedPreset)
        && String(this.pendingPresetId) !== String(this.currentPresetId);
    },
  },

  watch: {
    currentPresetId(value) {
      this.pendingPresetId = value;
    },
  },

  mounted() {
    document.addEventListener("mousedown", this.handleOutsideClick);
  },

  beforeUnmount() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
  },

  methods: {
    toggle() {
      if (this.readonly) return;

      this.open = !this.open;
      if (this.open) {
        this.pendingPresetId = this.currentPresetId;
        this.$emit("open");
      }
    },
    close() {
      this.open = false;
      this.pendingPresetId = this.currentPresetId;
    },
    confirm() {
      if (!this.hasPendingChange || this.isCalculating) return;
      this.$emit("switch", this.pendingPresetId);
      this.open = false;
    },
    handleOutsideClick(event) {
      if (this.open && !this.$refs.root?.contains(event.target)) this.close();
    },
    isSelected(preset) {
      return String(preset.id) === String(this.pendingPresetId);
    },
    isCurrent(preset) {
      return String(preset.id) === String(this.currentPresetId);
    },
    isDefault(preset) {
      return this.defaultPresetId !== null
        && String(preset.id) === String(this.defaultPresetId);
    },
    optionMark(preset) {
      if (this.isSelected(preset) && !this.isCurrent(preset)) return "✓";
      if (this.isCurrent(preset)) return "•";
      return "";
    },
    optionDescription(preset) {
      const resultName = preset.result_name
        ? `Итог — ${preset.result_name}`
        : "Результат калькулятора";
      return this.isCurrent(preset) ? `Текущий · ${resultName}` : resultName;
    },
  },
};
</script>

<style scoped>
.plan-calculator-selector {
  position: relative;
  display: flex;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 auto;
  align-items: baseline;
  justify-content: flex-end;
  gap: 6px;
  font-size: 12px;
}

.plan-calculator-selector__label {
  flex: none;
  color: #5b6070;
}

.plan-calculator-selector__trigger {
  min-width: 0;
  max-width: 220px;
  display: inline-flex;
  flex: 0 1 220px;
  align-items: baseline;
  gap: 3px;
  padding: 0;
  border: 0;
  border-bottom: 1px dashed #3b5bdb;
  background: transparent;
  color: #3b5bdb;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.plan-calculator-selector__trigger-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-calculator-selector__trigger-icon {
  flex: none;
}

.plan-calculator-selector__trigger:focus-visible {
  border-radius: 2px;
  outline: 2px solid rgba(59, 91, 219, 0.3);
  outline-offset: 2px;
}

.plan-calculator-selector__value {
  min-width: 0;
  max-width: 220px;
  overflow: hidden;
  font-size: 13px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-calculator-selector__popover {
  position: absolute;
  z-index: 20;
  top: calc(100% + 8px);
  right: 0;
  width: min(400px, calc(100vw - 48px));
  overflow: hidden;
  border: 1px solid #d5d9e4;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 14px 32px rgba(20, 24, 40, 0.18);
  color: #1e1e2f;
  text-align: left;
}

.plan-calculator-selector__heading {
  padding: 10px 14px 6px;
  color: #5b6070;
  font-size: 12px;
}

.plan-calculator-selector__options {
  max-height: 240px;
  overflow-y: auto;
}

.plan-calculator-selector__empty {
  padding: 12px 14px;
  color: #5b6070;
  font-size: 12px;
}

.plan-calculator-selector__option {
  width: 100%;
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  gap: 8px;
  padding: 8px 14px;
  border: 0;
  background: #fff;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.plan-calculator-selector__option:hover,
.plan-calculator-selector__option:focus-visible,
.plan-calculator-selector__option--selected {
  background: #eef2ff;
  outline: none;
}

.plan-calculator-selector__mark {
  color: #3b5bdb;
  font-weight: 700;
  text-align: center;
}

.plan-calculator-selector__option-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.plan-calculator-selector__option-name {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
  font-weight: 500;
}

.plan-calculator-selector__default {
  padding: 2px 5px;
  border-radius: 4px;
  background: #e8f0ff;
  color: #3156c8;
  font-size: 10px;
  font-weight: 500;
  line-height: 1.2;
}

.plan-calculator-selector__option small {
  color: #5b6070;
  font-size: 12px;
}

.plan-calculator-selector__warning {
  margin: 6px 14px 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fffbeb;
  color: #854d0e;
  font-size: 12px;
  line-height: 1.45;
}

.plan-calculator-selector__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 14px;
}

@media (max-width: 768px) {
  .plan-calculator-selector {
    justify-content: flex-start;
  }
  .plan-calculator-selector__trigger {
    max-width: 100%;
  }
  .plan-calculator-selector__popover {
    right: auto;
    left: 0;
  }
  .plan-calculator-selector__actions {
    flex-wrap: wrap;
  }
}
</style>
